/** Planner data shapes and date/load helpers shared by grid and list. */
import { ApiError } from "../../lib/api.js";

export type PlannerMode = "day" | "week" | "month";
export type PlannerResource = {
  id: string;
  firstName: string;
  lastName: string;
  workSchedules: Array<{ weekday: number; plannedMinutes: number }>;
};
export type PlannerProject = {
  id: string;
  number: string;
  title: string;
  status: string;
  parentId: string | null;
};
export type PlannerAssignment = {
  id: string;
  projectId: string;
  userId: string;
  startAt: string;
  endAt: string;
  localDate: string;
  startTime: string;
  endTime: string;
  notes: string | null;
  version: number;
  project: Pick<PlannerProject, "id" | "number" | "title">;
};
export type PlannerData = {
  timeZone: string;
  resources: PlannerResource[];
  assignments: PlannerAssignment[];
  backlog: PlannerProject[];
  teams: Array<{ id: string; name: string; color: string | null }>;
  holidays: Array<{ date: string; name: string }>;
  absences: Array<{
    userId: string;
    date: string;
    kind: string;
    minutes: number;
  }>;
};
export type PlannerConflict = {
  type: string;
  message: string;
  assignmentId?: string;
};
export type EditorState = {
  assignmentId: string | null;
  version: number | null;
  projectId: string;
  userId: string;
  localDate: string;
  startTime: string;
  endTime: string;
  notes: string;
};

export function startOfLocalDay(value: Date): Date {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
}

export function plannerDays(anchor: Date, mode: PlannerMode): Date[] {
  const start = startOfLocalDay(anchor);
  if (mode === "week")
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  else if (mode === "month") start.setDate(1);
  const end = new Date(start);
  if (mode === "day") end.setDate(end.getDate() + 1);
  if (mode === "week") end.setDate(end.getDate() + 7);
  if (mode === "month") end.setMonth(end.getMonth() + 1);
  const days: Date[] = [];
  for (const day = new Date(start); day < end; day.setDate(day.getDate() + 1))
    days.push(new Date(day));
  return days;
}

export function moveAnchor(
  anchor: Date,
  mode: PlannerMode,
  direction: -1 | 1,
): Date {
  const result = new Date(anchor);
  if (mode === "day") result.setDate(result.getDate() + direction);
  if (mode === "week") result.setDate(result.getDate() + 7 * direction);
  if (mode === "month") result.setMonth(result.getMonth() + direction, 1);
  return result;
}

export function minutesBetween(startAt: string, endAt: string) {
  return Math.round(
    (new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000,
  );
}

export function hours(minutes: number) {
  return `${Math.round((minutes / 60) * 10) / 10}h`;
}

export function parseConflicts(error: unknown): PlannerConflict[] {
  if (!(error instanceof ApiError) || error.code !== "PLANNER_CONFLICT")
    return [];
  try {
    const parsed: unknown = JSON.parse(error.message);
    return Array.isArray(parsed)
      ? parsed.filter(
          (item): item is PlannerConflict =>
            item !== null && typeof item === "object" && "message" in item,
        )
      : [];
  } catch {
    return [];
  }
}

/** Load bar colour: free, filling up, full, overbooked. */
export function loadClass(booked: number, capacity: number) {
  if (capacity === 0) return booked > 0 ? "bg-danger" : "bg-app-border";
  const ratio = booked / capacity;
  if (ratio > 1) return "bg-danger";
  if (ratio >= 0.9) return "bg-success";
  return "bg-info";
}
